import gql from 'graphql-tag';

// What canOpenHostFeed (helpers/is-game-host.js) reads, for the route's
// access check and the page's own.
export const accessFields = gql`
  fragment HostFeedAccessFields on User {
    id
    isAdmin
  }
`;

// Ported from waybetter-mobile's lib/shared/Social/fragments.js, trimmed to
// what this page renders.
const userFields = gql`
  fragment HostFeedUserFields on User {
    id
    displayName
    firstName
    avatarV2 {
      value
      type
    }
  }
`;

const postFields = gql`
  fragment HostFeedPostFields on Post {
    id
    content
    richContent {
      raw
    }
    createdAt
    publishAt
    pinned
    postType
    visibility
    parentId
    rootId
    removed
    commentCount
    reactionCount
    reactionCounts {
      like
      inspired
      onfire
      lovethis
      same
      supportive
    }
    userReactions {
      id
      kind
    }
    metadata {
      prompt
    }
    feeds {
      id
      metadata {
        hostIds
      }
    }
    media {
      edges {
        id
        metadata
      }
    }
    user {
      ...HostFeedUserFields
    }
  }
  ${userFields}
`;

const postListFields = gql`
  fragment HostFeedPostListFields on PostList {
    edges {
      ...HostFeedPostFields
      comments {
        edges {
          ...HostFeedPostFields
        }
      }
    }
    pageInfo {
      limit
      endCursor
      hasNextPage
    }
  }
  ${postFields}
`;

export const GAME_QUERY = gql`
  query HostFeedGame($gameId: Int!) {
    me {
      ...HostFeedUserFields
      ...HostFeedAccessFields
    }
    game(id: $gameId, type: falkor) {
      ... on FalkorGame {
        id
        title
        hostIds
        visibility
        subtypeVariation
      }
    }
  }
  ${userFields}
  ${accessFields}
`;

// waybetter-mobile's MySocialFeed: every pinned post, then the rest a page
// at a time. Hosts always get scoreVariant 1 (newest first) there too.
export const FEED_QUERY = gql`
  query HostFeedPosts(
    $gameId: Int
    $limit: PositiveInt
    $cursor: PositiveInt
    $scopes: [PostSearchScope]
  ) {
    pinnedPosts: posts(
      paging: { limit: 999 }
      search: {
        pinned: true
        scopes: $scopes
        gameType: falkor
        gameId: $gameId
        preloadComments: true
      }
    ) {
      ...HostFeedPostListFields
    }
    posts: posts(
      paging: { limit: $limit, cursor: $cursor, direction: after }
      search: {
        pinned: false
        scopes: $scopes
        gameType: falkor
        gameId: $gameId
        preloadComments: true
      }
      scoreVariant: 1
    ) {
      ...HostFeedPostListFields
    }
  }
  ${postListFields}
`;

// Not on mobile: the host's own posts that haven't gone out yet. The feed
// itself only returns posts whose publishAt has passed; showFuturePosts
// lifts that, and the page keeps the ones still in the future.
export const SCHEDULED_QUERY = gql`
  query HostFeedScheduledPosts($gameId: Int, $userId: PositiveInt) {
    scheduledPosts: posts(
      paging: { limit: 50 }
      search: {
        gameType: falkor
        gameId: $gameId
        userId: $userId
        depth: 0
        showFuturePosts: true
      }
    ) {
      edges {
        ...HostFeedPostFields
      }
    }
  }
  ${postFields}
`;

export const COMMENTS_QUERY = gql`
  query HostFeedComments(
    $id: Int!
    $limit: PositiveInt!
    $cursor: PositiveInt
    $sort: [PostSortColumn!]
  ) {
    post(id: $id, type: falkor) {
      id
      commentCount
      comments(
        paging: { limit: $limit, cursor: $cursor, direction: after }
        sort: $sort
      ) {
        edges {
          ...HostFeedPostFields
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${postFields}
`;

export const CREATE_POST = gql`
  mutation HostFeedCreatePost(
    $feeds: [PostFeedTypeInput!]
    $parentId: Int
    $userId: Int
    $content: String
    $media: JSON
    $pinned: Boolean
    $publishAt: String
    $visibility: SocialPostVisibility
    $sourceDevice: String
  ) {
    createPost(
      type: regular
      gameType: falkor
      feeds: $feeds
      parentId: $parentId
      userId: $userId
      content: $content
      media: $media
      pinned: $pinned
      publishAt: $publishAt
      visibility: $visibility
      sourceDevice: $sourceDevice
    ) {
      ...HostFeedPostFields
    }
  }
  ${postFields}
`;

export const UPDATE_POST = gql`
  mutation HostFeedUpdatePost(
    $postId: Int!
    $content: String
    $pinned: Boolean
    $publishAt: String
    $visibility: SocialPostVisibility
  ) {
    updatePost(
      gameType: falkor
      id: $postId
      content: $content
      pinned: $pinned
      publishAt: $publishAt
      visibility: $visibility
    ) {
      ...HostFeedPostFields
    }
  }
  ${postFields}
`;

export const TOGGLE_PINNED = gql`
  mutation HostFeedTogglePinned($id: Int!, $pinned: Boolean) {
    togglePinnedPost(id: $id, gameType: falkor, pinned: $pinned) {
      id
      pinned
    }
  }
`;

export const REMOVE_POST = gql`
  mutation HostFeedRemovePost($id: Int!) {
    removePost(id: $id, gameType: falkor)
  }
`;

export const CREATE_REACTION = gql`
  mutation HostFeedCreateReaction(
    $postId: Int!
    $userId: Int!
    $kind: ReactionKind!
  ) {
    createReaction(
      gameType: falkor
      postId: $postId
      userId: $userId
      kind: $kind
    ) {
      id
      userId
      kind
    }
  }
`;

export const DELETE_REACTION = gql`
  mutation HostFeedDeleteReaction($id: Int!) {
    deleteReaction(id: $id, gameType: falkor) {
      id
      kind
    }
  }
`;
